/**
 * [INPUT]: 依赖 charts/cartesian 的 dither BarChart 组合 API，消费有序 {key,label,value} 阶段数据
 * [OUTPUT]: 对外提供 FunnelChart，统一阶段用户数、环比或固定首层分母转化率、Tooltip、a11y 与 dither 动画
 * [POS]: charts 产品门面的有序转化图，将漏斗语义从业务页手写 meter 中收回设计系统
 * [PROTOCOL]: 变更时更新此头部，然后检查 AGENTS.md
 */

import { useMemo } from 'react'
import { Bar, BarChart, Grid, Tooltip, XAxis, YAxis } from './cartesian'

const DEFAULT_MARGINS = Object.freeze({ top: 18, right: 12, bottom: 24, left: 48 })

function FunnelTooltip({ heading, row, valueLabel, totalLabel, valueFormatter, rateMode }) {
  if (!row) return null
  return (
    <div className="min-w-[156px] space-y-1 text-xs">
      <div className="mb-1 border-b pb-1 font-medium">{heading}</div>
      <div className="flex items-center justify-between gap-6">
        <span className="text-muted-foreground">{valueLabel}</span>
        <span className="font-mono tabular-nums">{valueFormatter(row.value)}</span>
      </div>
      {rateMode === 'step' && (
        <div className="flex items-center justify-between gap-6">
          <span className="text-muted-foreground">上一阶段转化</span>
          <span className="font-mono tabular-nums">{formatRate(row.stepRate, 1)}</span>
        </div>
      )}
      <div className="flex items-center justify-between gap-6">
        <span className="text-muted-foreground">{totalLabel}</span>
        <span className="font-mono tabular-nums">{formatRate(row.totalRate, 1)}</span>
      </div>
    </div>
  )
}

const formatRate = (value, digits) => value == null ? '—' : `${value.toFixed(digits)}%`
const defaultValueFormatter = (value) => Number(value || 0).toLocaleString()

export function FunnelChart({
  data,
  valueLabel = '用户数',
  totalLabel = '总转化',
  valueFormatter = defaultValueFormatter,
  color = 'blue',
  variant = 'gradient',
  height = 250,
  margins = DEFAULT_MARGINS,
  ariaLabel = '有序转化阶段图',
  ariaDescription = '每根柱子表示到达当前阶段的数量，提示层展示阶段环比与总转化率。',
  rateMode = 'step',
}) {
  const chartData = useMemo(() => {
    const rows = Array.isArray(data) ? data : []
    const firstValue = Number(rows[0]?.value) || 0
    return rows.map((item, index) => {
      const value = Number(item.value) || 0
      const previousValue = index > 0 ? Number(rows[index - 1]?.value) || 0 : 0
      return {
        ...item,
        value,
        stepRate: index === 0 ? null : previousValue > 0 ? (value / previousValue) * 100 : null,
        totalRate: firstValue > 0 ? (value / firstValue) * 100 : null,
      }
    })
  }, [data])
  const config = useMemo(() => ({ value: { label: valueLabel, color } }), [color, valueLabel])
  const renderTooltip = (props) => (
    <FunnelTooltip
      {...props}
      valueLabel={valueLabel}
      totalLabel={totalLabel}
      valueFormatter={valueFormatter}
      rateMode={rateMode}
    />
  )

  return (
    <div data-slot="funnel-chart">
      <div className="w-full" style={{ height }}>
        <BarChart
          data={chartData}
          config={config}
          margins={margins}
          ariaLabel={ariaLabel}
          ariaDescription={ariaDescription}
        >
          <Grid />
          <XAxis dataKey="label" maxTicks={Math.max(1, chartData.length)} />
          <YAxis tickFormatter={(value) => valueFormatter(Math.round(value))} />
          <Tooltip labelKey="label" content={renderTooltip} />
          <Bar dataKey="value" variant={variant} isClickable />
        </BarChart>
      </div>
      {chartData.length > 1 && (
        <div
          className="mt-3 grid divide-x rounded-lg border bg-muted/20 px-2 py-2"
          style={{ gridTemplateColumns: `repeat(${chartData.length - 1}, minmax(0, 1fr))` }}
        >
          {chartData.slice(1).map((item, index) => (
            <div key={item.key || item.stage || item.name || item.label} className="px-2 text-center">
              <div className="truncate text-[10px] text-muted-foreground">
                {rateMode === 'total' ? `${item.label} / ${chartData[0]?.label}` : `${chartData[index]?.label} → ${item.label}`}
              </div>
              <div className="mt-0.5 font-mono text-xs font-semibold tabular-nums">
                {formatRate(rateMode === 'total' ? item.totalRate : item.stepRate, 1)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
